/**
 * Wire schemas for quote calculation endpoints.
 *
 * Full CurrentPolicyContext zod modelling lands in PR #4. For PR #3 we
 * validate the envelope and the pricing_summary on the response — the
 * fields the SPA merge logic depends on — while passing the opaque
 * context blob through to @cortex/engine for authoritative calculation.
 */

import { z } from 'zod';

export const PricingSummarySchema = z.object({
  netPremium: z.number(),
  grossPremium: z.number(),
  commissionAmount: z.number(),
  taxAmount: z.number(),
  warnings: z.array(z.string()),
  volumeDiscountUsed: z
    .object({
      source: z.enum(['Lives', 'Wageroll', 'None']),
      discount: z.number(),
    })
    .optional(),
});

/** Opaque quote payload — structural validation only until PR #4. */
export const PolicyContextSchema = z
  .object({
    categories: z.array(z.record(z.string(), z.unknown())),
    pricing_summary: PricingSummarySchema,
    policy_parameters: z.record(z.string(), z.unknown()),
    meta: z.record(z.string(), z.unknown()),
    quote_meta: z.record(z.string(), z.unknown()),
    client_profile: z.record(z.string(), z.unknown()),
  })
  .loose();

export const QuoteCalculateRequestSchema = z.object({
  context: PolicyContextSchema,
  config: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Summary of what the facility overlay did to the priced quote. NULL
 * when the calling user is an internal Lombard underwriter (no overlay
 * applies — the technical book rate is returned).
 *
 * Carried in the response so the SPA can show the broker which
 * concession / loading / clamp the facility caused, and the audit log
 * can join a future bind to the exact facility version that priced it.
 */
export const AppliedFacilitySchema = z.object({
  facilityId: z.string().uuid(),
  version: z.number().int().positive(),
  /** Multiplicative modifier on the technical net premium (1.0 = no change). */
  netPremiumMultiplier: z.number(),
  originalBrokeragePct: z.number(),
  clampedBrokeragePct: z.number(),
  commissionClampApplied: z.boolean(),
  originalDiscretionaryAdjustment: z.number(),
  clampedDiscretionaryAdjustment: z.number(),
  discretionaryClampApplied: z.boolean(),
});

export const QuoteCalculateResponseSchema = z.object({
  context: PolicyContextSchema,
  appliedFacility: AppliedFacilitySchema.nullable(),
});

/**
 * Bind request — SPA sends the full calculated context back to the API.
 * The API re-runs the engine to get an authoritative grossPremium and
 * then checks the caller's bind authority before confirming.
 */
export const QuoteBindRequestSchema = z.object({
  context: PolicyContextSchema,
  appliedFacility: AppliedFacilitySchema.nullable(),
});

/**
 * Bind confirmation returned on success.
 * `quoteRef` is `context.quote_meta.ref` for downstream audit joins.
 */
export const QuoteBindResponseSchema = z.object({
  quoteRef: z.string().min(1),
  boundAt: z.string().datetime(),
  grossPremium: z.number(),
  facilityId: z.string().uuid().nullable(),
  facilityVersion: z.number().int().positive().nullable(),
  boundByUserId: z.string().uuid(),
});

export type QuoteCalculateRequest = z.infer<typeof QuoteCalculateRequestSchema>;
export type QuoteCalculateResponse = z.infer<typeof QuoteCalculateResponseSchema>;
export type AppliedFacility = z.infer<typeof AppliedFacilitySchema>;
export type PricingSummary = z.infer<typeof PricingSummarySchema>;
export type QuoteBindRequest = z.infer<typeof QuoteBindRequestSchema>;
export type QuoteBindResponse = z.infer<typeof QuoteBindResponseSchema>;

/** Engine config response — opaque until PR #4 full schema. */
export const EngineConfigResponseSchema = z.object({
  config: z.record(z.string(), z.unknown()),
  version: z.string().min(1),
});

export type EngineConfigResponse = z.infer<typeof EngineConfigResponseSchema>;

// ─── Quote persistence schemas (PR #5e) ──────────────────────────────────

export const QuoteStatusSchema = z.enum(['Draft', 'Quoted', 'Bound', 'Referred', 'Expired']);

/**
 * Summary shape for list responses — no full context blob.
 * All monetary values are numeric strings (from Postgres numeric type).
 */
export const QuoteSummarySchema = z.object({
  id: z.string().uuid(),
  ref: z.string().min(1),
  productType: z.string().min(1),
  /** Insured client name (denormalised from context). */
  insuredName: z.string(),
  /** Broker display name (denormalised from context). */
  brokerDisplayName: z.string(),
  /** ISO currency code (denormalised from context). */
  currency: z.string(),
  grossPremium: z.string(),
  netPremium: z.string(),
  status: QuoteStatusSchema,
  createdByUserId: z.string().uuid(),
  createdByEntityId: z.string().uuid(),
  createdByEntityName: z.string(),
  createdByUserDisplayName: z.string(),
  facilityId: z.string().uuid().nullable(),
  expiresAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

/** Full shape including the context blob — used by GET /quotes/:ref. */
export const QuoteRecordSchema = QuoteSummarySchema.extend({
  context: PolicyContextSchema,
});

export const QuoteListResponseSchema = z.object({
  quotes: z.array(QuoteSummarySchema),
});

export const QuoteGetResponseSchema = z.object({
  quote: QuoteRecordSchema,
});

export const QuoteSaveStatusRequestSchema = z.object({
  ref: z.string().min(1),
  status: z.enum(['Quoted']),
});

export const QuoteSaveStatusResponseSchema = z.object({
  ref: z.string().min(1),
  status: QuoteStatusSchema,
});

export type QuoteStatus = z.infer<typeof QuoteStatusSchema>;
export type QuoteSummary = z.infer<typeof QuoteSummarySchema>;
export type QuoteRecord = z.infer<typeof QuoteRecordSchema>;
export type QuoteListResponse = z.infer<typeof QuoteListResponseSchema>;
export type QuoteGetResponse = z.infer<typeof QuoteGetResponseSchema>;
export type QuoteSaveStatusRequest = z.infer<typeof QuoteSaveStatusRequestSchema>;
export type QuoteSaveStatusResponse = z.infer<typeof QuoteSaveStatusResponseSchema>;
